/* Anomaly findings: what the statistics decided, and — separately, and only when a model was asked —
 * what it said about a decision already made.
 *
 * **Four verdicts, four appearances.** `insufficient_history` is a cold start and `undetermined` is a
 * series the maths did not apply to. Rendering either as "normal" would report an unmeasured table as
 * healthy; rendering `undetermined` as "anomalous" would page somebody about a table loading exactly
 * 1,000 rows a day. Both are the platform lying about a customer's data.
 *
 * **The design's layout: findings as cards on the left, the chosen one in a panel on the right.** The
 * panel reads the same finding the full detail page reads and links there for the detector, the
 * downstream reach and the threshold editor, so nothing the separate page could do was lost.
 *
 * **The priority filter narrows the list; it never hides a count.** The pills always show every
 * count including the zeros — "0 critical, 3 not yet measured" is the cold-start answer, and omitting
 * empty buckets would make it read like a healthy estate. A muted series stays in the list with its
 * tag: muting stops the paging, not the record.
 *
 * **The page is bounded.** `GET /findings` returns the 50 most recent and has no cursor, so on a
 * large estate there are older findings this screen cannot reach — the hint says "the 50 most recent"
 * rather than "50 findings" when the page is full.
 */

import { useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';

import { DatasetKindIcon, useDatasetNames } from '@/features/catalog-browser';
import { openCopilot } from '@/features/copilot';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import {
  FINDINGS_PAGE_SIZE,
  useClearExpected,
  useFinding,
  useFindings,
  useMarkExpected,
  useMuteSeries,
  useRaiseIncident,
  useUnmuteSeries,
} from '../api/anomaly';
import type {
  Explanation,
  FindingDetail,
  Irregularity,
  SeriesRef,
  StoredFinding,
  Verdict,
} from '../api/anomaly';
import {
  PRIORITY_FILTERS,
  basisKey,
  changeText,
  checkKey,
  expectedPhrase,
  explanationKey,
  isModelAuthored,
  matchesPriority,
  measureValue,
  primaryAction,
  priorityKey,
  signalKey,
  signalValue,
  sortedByPriority,
  subjectOf,
  verdictKey,
  windowLabel,
  zText,
} from '../model/presentation';
import type { PriorityFilter } from '../model/presentation';
import { SeriesChart } from './SeriesChart';

/** How long ago, in words, from an ISO instant — coarse on purpose (a finding list is scanned,
 * not read to the minute). */
function ago(iso: string, t: ReturnType<typeof useTranslation>['t']): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) return t('anomaly.detected.now');
  if (minutes < 60) return t('anomaly.detected.minutes', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('anomaly.detected.hours', { count: hours });
  return t('anomaly.detected.days', { count: Math.round(hours / 24) });
}

/* A verdict as the design's chip. `insufficient_history` wears the hollow dot: a cold start is an
 * absence of measurement, and a filled dot would claim a reading. */
function VerdictChip({
  finding,
  panel = false,
}: {
  readonly finding: StoredFinding;
  /** The panel's heading chip: larger and without the dot. */
  readonly panel?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <span
      className={
        panel ? 'lq-cn__state lq-an__verdict--panel' : 'lq-cn__state lq-an__verdict'
      }
      data-tone={CHIP_TONE[finding.verdict]}
      title={t(`${verdictKey(finding.verdict)}.meaning`)}
    >
      {panel ? null : (
        <span
          aria-hidden="true"
          className={
            finding.verdict === 'insufficient_history'
              ? 'lq-cn__dot lq-cn__dot--hollow'
              : 'lq-cn__dot'
          }
        />
      )}
      {finding.critical && finding.verdict === 'anomalous'
        ? t('anomaly.verdict.critical')
        : t(verdictKey(finding.verdict))}
    </span>
  );
}

/* The design's verdict chip tones: only an anomaly is the error hue. */
const CHIP_TONE = {
  anomalous: 'error',
  irregular: 'warn',
  normal: 'ok',
  undetermined: 'unknown',
  insufficient_history: 'unknown',
} as const satisfies Record<Verdict, string>;

function Authorship({
  explanation,
}: {
  readonly explanation: Explanation;
}): ReactElement {
  const { t } = useTranslation();
  const authored = isModelAuthored(explanation);
  return (
    <span
      className="lq-an__authorship"
      data-status={explanation.status}
      title={`${
        authored
          ? t('anomaly.explanation.byModel', { validator: explanation.validator ?? '' })
          : t('anomaly.explanation.byPlatform')
      } — ${t(explanationKey(explanation))}`}
    >
      {/* The design's short label on the chip; the full sentence — who wrote it and why — on the
          title, so a reader scanning cards gets the gist and a reader who wants the reason has it. */}
      {t(`anomaly.chip.${explanation.status}`)}
    </span>
  );
}

function FindingCard({
  finding,
  name,
  selected,
  onSelect,
}: {
  readonly finding: StoredFinding;
  readonly name: ReactNode;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const change = changeText(finding);
  const z = zText(finding);
  const subject = subjectOf(finding);
  const irregularity = finding.irregularity;
  return (
    <button
      type="button"
      className="lq-an__card"
      aria-pressed={selected}
      onClick={onSelect}
    >
      <span className="lq-an__cardHead">
        <VerdictChip finding={finding} />
        {/* What the row is about: the signal for a series, the column and its check for a
            point-in-time irregularity, which watches no series. */}
        <span className="lq-an__signal">
          {subject.kind === 'column' ? (
            <>
              <span className="lq-mono">{subject.column}</span> · {t(subject.key)}
            </>
          ) : (
            t(subject.key)
          )}
        </span>
        <span className="lq-an__spacer" />
        <span className="lq-an__when">{ago(finding.evaluated_at, t)}</span>
      </span>
      <span className="lq-an__dataset">
        <DatasetKindIcon datasetId={finding.dataset_id} />
        {name}
        {finding.muted ? (
          <span className="lq-dim"> · {t('anomaly.mutedTag')}</span>
        ) : null}
      </span>
      {irregularity != null ? (
        /* The current measurement against the bar it fails. No change, no deviation and no
           baseline: a point-in-time check compares with nothing in the past. */
        <span className="lq-an__figures">
          <strong className="lumiq-numeric">
            {measureValue(irregularity, irregularity.observed)}
          </strong>
          <span className="lq-an__vs">
            {t('anomaly.card.expected', { expected: expectedPhrase(irregularity) })}
          </span>
          <span className="lq-an__z">{t(basisKey(finding.basis))}</span>
        </span>
      ) : finding.signal === null ? null : (
        <span className="lq-an__figures">
          <strong className="lumiq-numeric">
            {signalValue(finding.signal, finding.observed)}
          </strong>
          <span className="lq-an__vs">
            {finding.baseline == null
              ? t('anomaly.card.vsNoBaseline')
              : t('anomaly.card.vs', {
                  baseline: signalValue(finding.signal, finding.baseline),
                })}
          </span>
          {change === null ? null : (
            <span
              className="lq-an__change"
              data-alert={finding.verdict === 'anomalous' ? 'true' : undefined}
            >
              {change}
            </span>
          )}
          <span className="lq-an__z">{z ?? t('anomaly.card.noDeviation')}</span>
        </span>
      )}
      <Authorship explanation={finding.explanation} />
    </button>
  );
}

/* The panel for a point-in-time irregularity: the current profile held to a stated check. It has no
 * series, so no chart, no baseline and none of the series actions (mute, mark expected) - the same
 * facts and the same one action the full detail page offers for it. */
function IrregularPanel({
  detail,
  irregularity,
  name,
}: {
  readonly detail: FindingDetail;
  readonly irregularity: Irregularity;
  readonly name: string;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="lq-an__panel">
      <div className="lq-an__panelHead">
        <div className="lq-an__panelTitle">
          <div className="lq-an__signal">
            <span className="lq-mono">{irregularity.column}</span> ·{' '}
            {t(checkKey(irregularity.check))}
          </div>
          <div className="lq-otype-title">
            <DatasetKindIcon datasetId={detail.dataset_id} />
            <h2 className="lq-an__panelName">{name}</h2>
          </div>
        </div>
        <VerdictChip finding={detail} panel />
      </div>

      <div className="lq-an__explain">
        <Authorship explanation={detail.explanation} />
        <p className="lq-an__explainText">{detail.explanation.text}</p>
      </div>

      <dl className="lq-an__facts">
        <div>
          <dt>{t('anomaly.detail.irregularity.current')}</dt>
          <dd className="lumiq-numeric">
            {measureValue(irregularity, irregularity.observed)}{' '}
            {t(`anomaly.measure.${irregularity.measure}`)}
          </dd>
        </div>
        <div>
          <dt>{t('anomaly.detail.irregularity.expected')}</dt>
          <dd className="lq-mono">{expectedPhrase(irregularity)}</dd>
        </div>
        <div>
          <dt>{t('anomaly.detail.irregularity.severity')}</dt>
          <dd>
            {detail.critical
              ? t('anomaly.detail.irregularity.critical')
              : t('anomaly.detail.irregularity.warning')}
          </dd>
        </div>
        <div>
          <dt>{t('anomaly.detail.irregularity.history')}</dt>
          <dd>{t('anomaly.detail.irregularity.historyUnavailable')}</dd>
        </div>
      </dl>

      <div className="lq-an__actions">
        <Link
          className="lq-btn lq-btn--secondary lq-btn--compact"
          to={generatePath(SCREEN_PATHS.dataset, { datasetId: detail.dataset_id })}
        >
          {t('anomaly.detail.viewDataset')}
        </Link>
      </div>
      <Link className="lq-an__full" to={`/anomalies/${encodeURIComponent(detail.id)}`}>
        {t('anomaly.panel.full')}
      </Link>
    </div>
  );
}

/* The design's sticky panel: one finding, its series, what was said about it and the four things a
 * reader can do. Reads the same `useFinding` the full detail page reads, so the two cannot disagree,
 * and links there for the detector, the downstream reach and the threshold editor. */
function FindingPanel({
  findingId,
  name,
}: {
  readonly findingId: string;
  readonly name: string;
}): ReactElement {
  const { t } = useTranslation();
  const finding = useFinding(findingId);
  const raiseIncident = useRaiseIncident();
  const mute = useMuteSeries();
  const unmute = useUnmuteSeries();
  const markExpected = useMarkExpected();
  const clearExpected = useClearExpected();

  if (finding.isPending) {
    return (
      <div
        className="lq-an__panel"
        role="status"
        aria-label={t('anomaly.detail.loading')}
      >
        <span className="lq-skeleton__bar lq-an__panelSkeleton" />
      </div>
    );
  }
  if (finding.isError) {
    return (
      <div className="lq-an__panel">
        <Alert tone="error" title={t('anomaly.detail.unreachable')}>
          {finding.error.message}
        </Alert>
      </div>
    );
  }

  const detail = finding.data;
  if (detail.irregularity != null) {
    return (
      <IrregularPanel detail={detail} irregularity={detail.irregularity} name={name} />
    );
  }
  const { signal, detector, sample_size: sampleSize } = detail;
  if (signal === null || detector === null || sampleSize === null) {
    /* A historical finding always has both. A row with neither is malformed, and saying so beats
       drawing a chart of nothing. */
    return (
      <div className="lq-an__panel">
        <Alert tone="warning">{t('anomaly.detail.malformed')}</Alert>
      </div>
    );
  }
  const series: SeriesRef = { datasetId: detail.dataset_id, signal };
  const state = detail.series_state;
  const incidentId = detail.incident_id ?? raiseIncident.data?.incident_id ?? null;
  const failure = raiseIncident.error ?? mute.error ?? unmute.error ?? null;

  return (
    <div className="lq-an__panel">
      <div className="lq-an__panelHead">
        <div className="lq-an__panelTitle">
          <div className="lq-an__signal">{t(signalKey(signal))}</div>
          <div className="lq-otype-title">
            <DatasetKindIcon datasetId={detail.dataset_id} />
            <h2 className="lq-an__panelName">{name}</h2>
          </div>
        </div>
        <VerdictChip finding={detail} panel />
      </div>

      {detail.series.length < 2 ? (
        <p className="lq-muted lq-nomargin">
          {t('anomaly.detail.chart.tooFew', { count: detail.series.length })}
        </p>
      ) : (
        <SeriesChart
          points={detail.series}
          band={detail.baseline_band}
          label={t('anomaly.detail.chart.label')}
          format={(value) => signalValue(signal, value)}
        />
      )}

      <div className="lq-an__explain">
        <Authorship explanation={detail.explanation} />
        <p className="lq-an__explainText">{detail.explanation.text}</p>
        {detail.explanation.rejection_reason != null ? (
          <p className="lq-an__rejected">
            {t('anomaly.panel.rejected', {
              reason: detail.explanation.rejection_reason.replace(/_/g, ' '),
            })}
          </p>
        ) : null}
      </div>

      <dl className="lq-an__facts">
        <div>
          <dt>{t('anomaly.panel.observedFact')}</dt>
          <dd className="lumiq-numeric">{signalValue(signal, detail.observed)}</dd>
        </div>
        <div>
          <dt>{t('anomaly.panel.baseline')}</dt>
          <dd className="lumiq-numeric">
            {detail.baseline == null
              ? t('anomaly.panel.notYet')
              : signalValue(signal, detail.baseline)}
          </dd>
        </div>
        <div>
          <dt>{t('anomaly.panel.deviation')}</dt>
          <dd className="lumiq-numeric">
            {detail.deviation == null || !Number.isFinite(detail.deviation)
              ? t('anomaly.panel.notComputable')
              : t('anomaly.panel.mad', { value: detail.deviation.toFixed(1) })}
          </dd>
        </div>
        <div>
          <dt>{t('anomaly.panel.threshold')}</dt>
          <dd>
            {t(
              detector.threshold_override === null
                ? 'anomaly.panel.thresholdDefault'
                : 'anomaly.panel.thresholdOverride',
              {
                threshold: detector.threshold_override ?? detector.threshold,
                critical: detector.critical_threshold,
              },
            )}
          </dd>
        </div>
        <div>
          <dt>{t('anomaly.detail.detector.window')}</dt>
          <dd>{windowLabel(detector)}</dd>
        </div>
        <div>
          <dt>{t('anomaly.panel.sample')}</dt>
          <dd className="lumiq-numeric">
            {t('anomaly.panel.sampleValue', { count: sampleSize })}
          </dd>
        </div>
      </dl>

      {state.muted ? (
        <p className="lq-muted lq-nomargin">{t('anomaly.detail.mutedNote')}</p>
      ) : null}
      {state.expected ? (
        <p className="lq-muted lq-nomargin">{t('anomaly.detail.expectedNote')}</p>
      ) : null}
      {failure !== null ? <Alert tone="error">{failure.message}</Alert> : null}

      <div className="lq-an__actions">
        {/* The incident this finding opened, itself — not the queue. A finding the statistics call
            anomalous with no incident yet can still raise one. */}
        {incidentId !== null ? (
          <Link
            className="lq-btn lq-btn--primary lq-btn--compact"
            to={`${SCREEN_PATHS.incidents}/${incidentId}`}
          >
            {t('anomaly.panel.openIncident')}
          </Link>
        ) : primaryAction(detail.verdict) === 'raise-incident' ? (
          <Button
            tone="primary"
            size="compact"
            busy={raiseIncident.isPending}
            onClick={() => {
              raiseIncident.mutate(detail.id);
            }}
          >
            {t('anomaly.detail.raiseIncident')}
          </Button>
        ) : null}
        <Link
          className="lq-btn lq-btn--secondary lq-btn--compact"
          to={generatePath(SCREEN_PATHS.dataset, { datasetId: detail.dataset_id })}
        >
          {t('anomaly.panel.openDataset')}
        </Link>
        <Button
          size="compact"
          busy={markExpected.isPending || clearExpected.isPending}
          onClick={() => {
            if (state.expected) clearExpected.mutate(series);
            else markExpected.mutate(series);
          }}
        >
          {state.expected
            ? t('anomaly.panel.expected')
            : t('anomaly.detail.markExpected')}
        </Button>
        <Button
          size="compact"
          busy={mute.isPending || unmute.isPending}
          onClick={() => {
            if (state.muted) unmute.mutate(series);
            else mute.mutate(series);
          }}
        >
          {state.muted ? t('anomaly.detail.unmute') : t('anomaly.detail.mute')}
        </Button>
        <span className="lq-an__spacer" />
        {/* Opens the shell's drawer with the question in the composer, unsent. */}
        <button
          type="button"
          className="lq-btn lq-btn--copilot lq-btn--compact"
          onClick={() => {
            openCopilot({
              question: t('anomaly.panel.askQuestion', {
                series: `${name} ${t(signalKey(signal)).toLowerCase()}`,
              }),
            });
          }}
        >
          {t('anomaly.panel.ask')}
        </button>
      </div>
      <Link className="lq-an__full" to={`/anomalies/${encodeURIComponent(detail.id)}`}>
        {t('anomaly.panel.full')}
      </Link>
    </div>
  );
}

export function AnomaliesScreen(): ReactElement {
  const { t } = useTranslation();
  const names = useDatasetNames();
  const findings = useFindings();
  const [filter, setFilter] = useState<PriorityFilter>('all');
  const [picked, setPicked] = useState<string | null>(null);

  const all = useMemo<readonly StoredFinding[]>(
    () => findings.data ?? [],
    [findings.data],
  );
  const shown = useMemo(
    () => sortedByPriority(all.filter((finding) => matchesPriority(finding, filter))),
    [all, filter],
  );
  /* The picked finding while it is still in view, else the first one: the panel is never empty
     while there is a finding to show, and never shows one the filter has hidden. */
  const selected = shown.find((finding) => finding.id === picked) ?? shown[0] ?? null;

  const nameOf = (finding: StoredFinding): string =>
    names.data?.byId.get(finding.dataset_id) ?? t('anomaly.card.unlisted');

  return (
    <>
      <PageHeader
        title={t('anomaly.title')}
        help={t('help.feature.anomalies')}
        lede={t('anomaly.lede')}
      />

      <div className="lq-stack">
        {findings.isError && (
          <Alert tone="error" title={t('anomaly.unreadable')}>
            {findings.error.message}
          </Alert>
        )}

        {findings.isPending && <SkeletonRows rows={5} columns={3} />}

        {findings.isSuccess && (
          <>
            {/* **The priority pills carry every count, zeros included.** "0 critical, 3 not yet
                measured" is the cold-start answer, and a pill row that dropped its empty buckets
                would read like a healthy estate. The pill narrows the list; it never hides a count. */}
            <div
              className="lq-an__pills"
              role="group"
              aria-label={t('anomaly.filter.label')}
            >
              {PRIORITY_FILTERS.map((value) => {
                const count =
                  value === 'all'
                    ? all.length
                    : all.filter((finding) => finding.priority === value).length;
                return (
                  <button
                    type="button"
                    key={value}
                    className="lq-an__pill"
                    aria-pressed={filter === value}
                    onClick={() => {
                      setFilter(value);
                    }}
                  >
                    {value === 'all' ? t('anomaly.filter.all') : t(priorityKey(value))}{' '}
                    <span className="lq-an__pillCount lumiq-numeric">{count}</span>
                  </button>
                );
              })}
            </div>

            {all.length === 0 ? (
              <EmptyState
                title={t('anomaly.emptyTitle')}
                guidance={t('anomaly.emptyGuidance')}
              />
            ) : (
              <div className="lq-an__layout">
                <div className="lq-an__list">
                  {shown.length === 0 ? (
                    <div className="lq-an__none">{t('anomaly.filteredEmptyTitle')}</div>
                  ) : (
                    shown.map((finding) => (
                      <FindingCard
                        key={finding.id}
                        finding={finding}
                        name={
                          names.data?.byId.has(finding.dataset_id) === true ? (
                            nameOf(finding)
                          ) : (
                            <span className="lq-dim">{nameOf(finding)}</span>
                          )
                        }
                        selected={selected?.id === finding.id}
                        onSelect={() => {
                          setPicked(finding.id);
                        }}
                      />
                    ))
                  )}
                  {/* The page is bounded: said only when it is full, because then there are older
                      findings this list cannot reach. */}
                  {all.length >= FINDINGS_PAGE_SIZE ? (
                    <p className="lq-an__note">
                      {t('anomaly.recentHintCapped', {
                        total: all.length,
                        shown: shown.length,
                      })}
                    </p>
                  ) : null}
                </div>
                {selected === null ? null : (
                  <FindingPanel
                    key={selected.id}
                    findingId={selected.id}
                    name={nameOf(selected)}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
